import { breakpointSizes, Button, Container } from "@brightagency/headstart-common";
import * as qs from "query-string";
import React, { ChangeEvent, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { WPImage, WPTaxTerm } from "../../../constants/types";
import { useTaxonomies } from "../../../services/app-state";
import { removeEmptyStringValues, useSuburbName } from "../../../utils";
import { Grid, GridBreakpoints } from "../../molecules/Grid";
import { RelatedLocalCta } from "../../molecules/RegionSelectCta";
import { CustomSelect } from "./CustomSelect";
import { Outer, Inner, Title, Fields, Actions, LinkButton, Spacer } from "./styled";

interface Props {
    title: string;
    image: WPImage;
}

export interface DirectoryQueryData {
    community: string;
    condition: string;
    age: string;
    gender: string;
}

export const useDirectoryQuery = (): DirectoryQueryData => {
    const { search } = useHistory().location;
    return React.useMemo(() => {
        return getDirectoryQuery(search);
    }, [search]);
};

export const getDirectoryQuery = (search: string): DirectoryQueryData => {
    const query = new URLSearchParams(search);
    return {
        community: query.get("community") ?? "",
        condition: query.get("condition") ?? "",
        age: query.get("age") ?? "",
        gender: query.get("gender") ?? ""
    };
};

const mapTaxTermToSelectOption = ({ name, slug }: WPTaxTerm) => ({
    label: name,
    value: slug,
});

export const HeroDirectory: React.FC<Props> = props => {
    const history = useHistory();
    const [values, setValues] = React.useState(getDirectoryQuery(window.location.search));
    const [taxonomies] = useTaxonomies();
    const suburbName = useSuburbName();

    const handleInputChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
        event.persist();
        setValues(old => ({
            ...old,
            [event.target.name]: event.target.value,
        }));
    }, []);

    const handleApply = useCallback(() => {
        const params = qs.stringify(removeEmptyStringValues(values), {
            encode: false,
        });
        history.push(`${history.location.pathname}?${params}`);
    }, [history, values]);

    const handleReset = useCallback(() => {
        history.push(history.location.pathname);
        setValues({ community: "", condition: "", age: "", gender: "" });
    }, [history]);

    const gridBreakpoints: GridBreakpoints = [
        { breakpointWidth: 0, columns: 1, gutter: 10 },
        { breakpointWidth: breakpointSizes.medium, columns: 3, gutter: 16 },
    ];

    return (
        <div className={suburbName ? 'blue-bg' : ''}>
        {suburbName ?
            <Outer>
                <Container>
                    <Inner>
                        <Title>{props.title} for {suburbName}</Title>
                        <Fields>
                            <Grid breakpoints={gridBreakpoints}>
                                <CustomSelect
                                    name="gender"
                                    label="Filter by gender"
                                    value={values.gender}
                                    onChange={handleInputChange}
                                    options={taxonomies.genders.map(mapTaxTermToSelectOption)}
                                />
                                <CustomSelect
                                    name="community"
                                    label="Filter by community"
                                    value={values.community}
                                    onChange={handleInputChange}
                                    options={taxonomies.communities.map(mapTaxTermToSelectOption)}
                                />
                                <CustomSelect
                                    name="condition"
                                    label="Filter by disorder or concern"
                                    value={values.condition}
                                    onChange={handleInputChange}
                                    options={taxonomies.conditions.map(mapTaxTermToSelectOption)}
                                />
                            </Grid>
                        </Fields>
                        <Actions>
                            <Button id="apply" onClick={handleApply} maxWidth={-1} appearance="secondary">
                                Apply
                            </Button>
                            <LinkButton onClick={handleReset}>Reset filters</LinkButton>
                        </Actions>
                    </Inner>
                </Container>
            </Outer>
            :
            <>
                <div className="blue-bg">
                    <Container>
                        <Inner>
                            <Title>{props.title}</Title>
                        </Inner>
                    </Container>
                </div>
                <Spacer />
                <RelatedLocalCta bottomSpacing={false} />
            </>
        }
        </div>
    );
};
